import { CardStatus } from '@seapoint/server/enums';
import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../../../design-system/button/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerTitle,
} from '../../../../design-system/drawer/drawer';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { AccountView, CardView, TransactionView } from '../../../../types/entity-types';
import { useCancelCard } from '../../hooks/use-cancel-card';
import { useSecureCardDetails } from '../../hooks/use-secure-card-details';
import { useUpdateCardDisplayName } from '../../hooks/use-update-card-display-name';
import { formatExpiry } from '../../utils/format-expiry';
import { CardActionsMenu } from '../card-actions-menu';
import { CardPreview } from '../card-preview/card-preview';
import { CardStatusBadge } from '../card-status-badge';
import { CancelCardDialog } from './cancel-card-dialog';
import { CardDetails } from './card-details';
import { CardTransactions } from './card-transactions/card-transactions';
import { EditCardNicknameDialog } from './edit-card-nickname-dialog';

type CardDetailsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  card: CardView;
  accounts: AccountView[];
  transactions: TransactionView[];
};

export const CardDetailsDrawer = ({ isOpen, onClose, card, accounts, transactions }: CardDetailsDrawerProps) => {
  const { details, isLoading, revealDetails, copyToClipboard, reset } = useSecureCardDetails();
  const { updateDisplayName, isUpdating } = useUpdateCardDisplayName();
  const { cancelCard, isCancelling } = useCancelCard();
  const [isEditNicknameDialogOpen, setIsEditNicknameDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  // Reset revealed details when card changes or drawer closes
  useEffect(() => {
    reset();
  }, [card.id, reset]);

  const panLast4 = card.maskedPan.slice(-4);
  const formattedExpiry = formatExpiry(card.expiry);

  const cardTransactions = transactions.filter((t) => t.cardId === card.id);

  // Determine what to show based on whether details have been revealed
  // PAN and CVV2 are from Modulr secure endpoint, expiry is from card data
  const isRevealed = !!details;
  const displayPan = details?.pan || panLast4;
  const displayExpiry = isRevealed ? formattedExpiry : undefined;
  const displayCvv2 = details?.cvv2;

  const handleReveal = () => {
    revealDetails(card.id);
  };

  const handleCopyPan = () => {
    if (details?.pan) {
      copyToClipboard(details.pan);
    }
  };

  const handleCopyExpiry = () => {
    if (isRevealed) {
      copyToClipboard(formattedExpiry);
    }
  };

  const handleCopyCvv2 = () => {
    if (details?.cvv2) {
      copyToClipboard(details.cvv2);
    }
  };

  const handleEditNickname = () => {
    setIsEditNicknameDialogOpen(true);
  };

  const handleSaveNickname = async (displayName: string) => {
    await updateDisplayName(card.id, displayName);
    setIsEditNicknameDialogOpen(false);
  };

  const handleCancelCard = () => {
    setIsCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    await cancelCard(card.id);
    setIsCancelDialogOpen(false);
    onClose();
  };

  const cardholderName = `${card.holderFirstName} ${card.holderLastName}`.trim();
  const defaultCardNickname = `${cardholderName}'s card`;
  const currentDisplayName = card.displayName || defaultCardNickname;
  const cardTitle = currentDisplayName;

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()} direction="right">
      <DrawerOverlay className="bg-foreground/20" />
      <DrawerContent
        className="fixed top-4 right-4 bottom-4 h-auto w-[384px] overflow-hidden rounded-lg border border-border bg-background p-0 shadow-lg outline-none data-[vaul-drawer-direction=right]:min-w-[384px] data-[vaul-drawer-direction=right]:sm:max-w-[384px]"
        aria-describedby={undefined}
      >
        <Stack space={6} align="stretch" className="h-full overflow-y-auto px-6 pt-0 pb-16">
          <DrawerHeader className="relative bg-background p-0 pt-6">
            <Inline align="center" justify="between" className="w-full">
              <Inline align="center" space={2}>
                <DrawerTitle className="text-lg font-semibold text-foreground">{cardTitle}</DrawerTitle>
                <CardStatusBadge status={card.status} />
              </Inline>
              <DrawerClose className="static top-auto right-auto text-muted-foreground hover:text-foreground" />
            </Inline>
          </DrawerHeader>

          <Inline justify="center">
            <CardPreview
              pan={displayPan}
              expiry={displayExpiry}
              cvv2={displayCvv2}
              isLoading={isLoading}
              onReveal={handleReveal}
              onCopyPan={handleCopyPan}
              onCopyExpiry={handleCopyExpiry}
              onCopyCvv2={handleCopyCvv2}
              className="w-full max-w-[336px]"
            />
          </Inline>

          <Inline align="center" justify="end" space={2} className="w-full">
            <Button
              variant="secondary"
              size="sm"
              className="rounded-full"
              onClick={handleCancelCard}
              disabled={card.status === CardStatus.CANCELLED}
            >
              <Inline space={2} align="center">
                <Trash2 size={16} />
                <span>Cancel card</span>
              </Inline>
            </Button>

            <CardActionsMenu cardStatus={card.status} onEditNickname={handleEditNickname} />
          </Inline>

          <CancelCardDialog
            isOpen={isCancelDialogOpen}
            onClose={() => setIsCancelDialogOpen(false)}
            onConfirm={handleConfirmCancel}
            cardLast4={panLast4}
            isLoading={isCancelling}
          />

          <EditCardNicknameDialog
            isOpen={isEditNicknameDialogOpen}
            onClose={() => setIsEditNicknameDialogOpen(false)}
            onSave={handleSaveNickname}
            currentDisplayName={currentDisplayName}
            defaultNickname={defaultCardNickname}
            isLoading={isUpdating}
          />

          <CardDetails card={card} accounts={accounts} />

          <CardTransactions transactions={cardTransactions} />
        </Stack>
      </DrawerContent>
    </Drawer>
  );
};
